import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Search, UserPlus, Mail, X } from "lucide-react";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Badge } from "@/shared/components/ui/badge";
import { useToast } from "@/shared/hooks/use-toast";
import { ProfileService } from "@/features/Profile/services/profileService";
import { UserProfile } from "@/features/Profile/types/profileTypes";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { InviteUserDialog } from "@/features/Users/components/InviteUserDialog";
import { InviteService } from "@/features/Users/services/inviteService";
import { useWorkspaceUserIds, useUpdateWorkspaceUsers } from "@/features/Workspaces/hooks/useWorkspaceUsers";
import { useOrganization } from "@/shared/hooks/api/useOrganization";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";

interface AddWorkspaceUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

export function AddWorkspaceUsersDialog({ open, onOpenChange, workspaceId }: AddWorkspaceUsersDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: userOrganization } = useOrganization();
  const { selectedOrganization } = useSuperAdminOrg();
  // Entra na queryKey abaixo: sem o id da organização ativa, o cache da
  // organização anterior sobreviveria à troca de organização.
  const effectiveOrgId = selectedOrganization?.id ?? userOrganization?.organization_id;

  const [searchQuery, setSearchQuery] = useState("");
  // Tracks ONLY the new users being added in this session.
  // Already-linked workspace members are rendered as locked rows (see
  // memberIdSet below) and do not participate in this state — preserving
  // them is the responsibility of the save handler, which merges the
  // existing membership with the new additions before submitting.
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [cancelingInviteId, setCancelingInviteId] = useState<string | null>(null);

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination({
    initialPage: 1,
    initialPageSize: 5,
  });

  const { data: organizationUsers = [], isLoading: isLoadingUsers } = useQuery<UserProfile[]>({
    queryKey: ["organization-users", effectiveOrgId],
    queryFn: async () => {
      const users = await ProfileService.getCurrentOrganizationUsers();
      // Somente ativos. Superadmins da org APARECEM como candidatos (decisão
      // 2026-07-02): o modelo permite superadmin como membro de workspace
      // (product_users) — o backend já esconde superadmins de requesters não
      // superadmin, então quem chega aqui é elegível.
      return users ? users.filter((u) => u.status === "active") : [];
    },
    staleTime: 60000,
    enabled: !!user?.id,
  });

  const { data: workspaceUserIds = [] } = useWorkspaceUserIds(workspaceId);
  const memberIdSet = useMemo(() => new Set(workspaceUserIds), [workspaceUserIds]);

  const { data: organizationProducts = [] } = useQuery({
    queryKey: ["organization-products", effectiveOrgId],
    queryFn: () => WorkspaceService.getCurrentOrganizationProducts(),
    staleTime: 60000,
    enabled: !!user?.id,
  });

  const { data: pendingInvites = [] } = useQuery({
    queryKey: ["workspace-invites", workspaceId],
    queryFn: async () => {
      const allInvites = await InviteService.listInvites();
      return allInvites.filter(
        (inv) => inv.status === "pending" && Array.isArray(inv.product_ids) && inv.product_ids.includes(workspaceId)
      );
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { mutate: cancelInvite } = useMutation({
    mutationFn: (inviteId: string) => InviteService.deleteInvite(inviteId),
    onMutate: (inviteId) => {
      setCancelingInviteId(inviteId);
    },
    onSuccess: () => {
      setCancelingInviteId(null);
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "workspace-invites" });
      queryClient.invalidateQueries({ queryKey: ["invites"] });
      toast({ title: t("settings.users.inviteCancelSuccess"), description: t("settings.users.inviteCancelSuccessDescription") });
    },
    onError: () => {
      setCancelingInviteId(null);
      toast({ variant: "destructive", title: t("settings.users.inviteCancelError"), description: t("settings.users.inviteCancelErrorDescription") });
    },
  });

  // Reset session selections when dialog closes so the next open starts
  // empty. Already-linked members are derived from workspaceUserIds at
  // render time, so they don't need to be re-initialized here.
  useEffect(() => {
    if (!open) {
      setSelectedUserIds([]);
      setSearchQuery("");
    }
  }, [open]);

  const { mutate: updateUsers, isPending: isSaving } = useUpdateWorkspaceUsers();

  const filteredUsers = useMemo(() => {
    return organizationUsers.filter((u) => {
      if (!searchQuery.trim()) return true;
      const lower = searchQuery.toLowerCase();
      return (u.full_name?.toLowerCase().includes(lower) ?? false) || u.email.toLowerCase().includes(lower);
    });
  }, [organizationUsers, searchQuery]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paginatedUsers = useMemo(() => paginateData(filteredUsers), [filteredUsers, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase();

  const handleToggleUser = (userId: string, checked: boolean) => {
    setSelectedUserIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const handleSave = () => {
    // Backend endpoint replaces the full membership list, so we must send
    // existing members + newly selected together — otherwise everyone
    // already in the workspace would be unlinked on save.
    const mergedUserIds = Array.from(new Set([...workspaceUserIds, ...selectedUserIds]));
    updateUsers(
      { workspaceId, userIds: mergedUserIds },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user-products"] });
          queryClient.invalidateQueries({ queryKey: ["organization-users"] });
          toast({
            title: t("settings.users.updateWorkspacesSuccess"),
            description: t("settings.users.updateWorkspacesSuccessDescription"),
          });
          onOpenChange(false);
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: t("settings.users.updateWorkspacesError"),
            description: t("settings.users.updateWorkspacesErrorDescription"),
          });
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("settings.workspaces.addUsersButton")}</DialogTitle>
            <DialogDescription>{t("settings.workspaces.inviteUserCta")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Email invite section */}
            <div className="flex flex-col items-center justify-center p-4 border border-dashed rounded-md bg-muted/30">
              <p className="text-sm text-muted-foreground text-center mb-3">
                {t("settings.workspaces.inviteUserCta")}
              </p>
              <Button
                variant="outline"
                onClick={() => setInviteDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <UserPlus className="h-4 w-4" />
                {t("settings.users.inviteButton")}
              </Button>
            </div>

            {/* Pending invites */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">{t("settings.workspaces.pendingInvites")}</h3>
                  <Badge variant="secondary">{pendingInvites.length}</Badge>
                </div>
                <div className="border rounded-md p-3 bg-muted/30 space-y-3">
                  {pendingInvites.map((invite, index) => (
                    <div key={invite.id}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 flex-1">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {(invite.user_name || invite.email)?.[0]?.toUpperCase() ?? "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium">{invite.user_name || invite.email}</p>
                            <p className="text-xs text-muted-foreground">{invite.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {t("settings.users.awaitingAccess")}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label={t("settings.users.cancelInvite")}
                            onClick={() => cancelInvite(invite.id)}
                            disabled={cancelingInviteId === invite.id}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {index < pendingInvites.length - 1 && <div className="border-t mt-3" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registered users */}
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : organizationUsers.length === 0 ? (
              <div className="p-4 text-center border rounded-md">
                <p className="text-sm text-muted-foreground">{t("settings.workspaces.noUsersInOrg")}</p>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={t("settings.workspaces.searchUsers")}
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center border rounded-md">
                    <p className="text-muted-foreground">{t("settings.workspaces.noUsersFound")}</p>
                  </div>
                ) : (
                  <>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12"><span className="sr-only">{t('settings.workspaces.fields.avatar')}</span></TableHead>
                            <TableHead>{t("settings.workspaces.fields.userName")}</TableHead>
                            <TableHead className="w-[100px]">{t("settings.workspaces.fields.access")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedUsers.map((orgUser) => {
                            const isMember = memberIdSet.has(orgUser.id);
                            return (
                              <TableRow key={orgUser.id} className={isMember ? "opacity-60" : undefined}>
                                <TableCell>
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage
                                      src={orgUser.avatar_url || ""}
                                      alt={orgUser.full_name || orgUser.email}
                                    />
                                    <AvatarFallback>
                                      {getInitials(orgUser.full_name || orgUser.email)}
                                    </AvatarFallback>
                                  </Avatar>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">
                                        {orgUser.full_name || t("settings.workspaces.noName")}
                                      </span>
                                      {orgUser.id === user?.id && (
                                        <span className="text-xs text-muted-foreground">
                                          {t("settings.workspaces.meLabel")}
                                        </span>
                                      )}
                                      {isMember && (
                                        <Badge variant="secondary" className="text-xs">
                                          {t("settings.workspaces.alreadyMember")}
                                        </Badge>
                                      )}
                                    </div>
                                    <span className="text-xs text-muted-foreground">{orgUser.email}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    id={`add-user-${orgUser.id}`}
                                    checked={isMember || selectedUserIds.includes(orgUser.id)}
                                    disabled={isMember}
                                    aria-label={
                                      isMember
                                        ? t("settings.workspaces.alreadyMember")
                                        : undefined
                                    }
                                    onCheckedChange={(checked) =>
                                      handleToggleUser(orgUser.id, checked as boolean)
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    <DataPagination
                      currentPage={currentPage}
                      pageSize={pageSize}
                      totalItems={filteredUsers.length}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      pageSizeOptions={[5, 10, 20, 50]}
                    />
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        products={organizationProducts}
        organizationId={effectiveOrgId}
        preselectedProductId={workspaceId}
      />
    </>
  );
}
